"use client";

import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";

import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { endOfDay, format, startOfDay } from "date-fns";

import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import OrdersListHeader from "@/components/common/orders/OrdersListHeader";
import { updateStatusByTotal } from "@/utils/functions";
import OrderDetails from "../orders/OrderDetails";
import { ResourceContextProvider } from "react-admin";
import { DatePickerWithRange } from "@/components/custom/date-picker-with-range";
import { Input } from "@/components/ui/input";
import { Orders } from "@/interface/entityInterface";

export type Order = {
  id: number;
  client_name: string;
  client_phone: string;
  pickup: string;
  dropoff: string;
  status: "NEW_ORDER" | "ACCEPTED" | "REJECTED";
  created_at: string;
};

export const OrdersList = () => {
  const [selectedOrder, setSelectedOrder] = React.useState<any | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);

  const [selectedTab, setSelectedTab] = React.useState("ALL");
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [endDate, setEndDate] = React.useState<Date>(new Date());
  const [client, setClient] = React.useState("");

  const [statusMenu, setStatusMenu] = React.useState([]);
  const [totalOrders, setTotalOrders] = React.useState(0);
  const statusItems = [
    { label: "New Orders", status: "NEW_ORDER" },
    { label: "Approved", status: "ACCEPTED" },
    { label: "Rejected", status: "REJECTED" },
    { label: "Cancelled", status: "CANCELLED" },
    { label: "Completed", status: "COMPLETED" },
  ];

  React.useEffect(() => {
    updateStatusByTotal(
      startOfDay(selectedDate),
      endOfDay(endDate),
      setStatusMenu,
      setTotalOrders,
      statusItems,
      true,
    );
  }, [selectedDate, endDate]);

  const handleRowClick = (row: Orders) => {
    setIsSheetOpen(true);
    setSelectedOrder(row);
  };

  const columns: ColumnDef<Orders>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div className="capitalize">{row.getValue("id")}</div>,
    },
    {
      accessorKey: "pickup",
      header: "Pickup",
      cell: ({ row }) => (
        <div className="capitalize">
          <p>{row.original.pickup}</p>
        </div>
      ),
    },
    {
      accessorKey: "dropoff",
      header: "Dropoff",
      cell: ({ row }) => {
        return (
          <div className="capitalize">
            <p>{row.original.dropoff}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created at",
      cell: ({ row }) => (
        <div className="capitalize">
          <p>
            {row.getValue("created_at")
              ? format(row.getValue("created_at"), "dd MMM y, HH:mm:ss")
              : ""}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "client_name",
      header: "Client",
      cell: ({ row }) => <div>{row.getValue("client_name")}</div>,
    },
    {
      accessorKey: "pickup_time",
      header: "Pickup Time",
      cell: ({ row }) => (
        <div className="capitalize">
          <p>
            {row.getValue("pickup_time")
              ? format(row.getValue("pickup_time"), "dd MMM y, HH:mm:ss")
              : ""}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "order_type",
      header: "Type",
      cell: ({ row }) => (
        <div className="w-32">
          <Badge
            variant="outline"
            className="px-1.5 text-muted-foreground capitalize"
          >
            {row.original.order_type}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
        >
          {row.original.status === "NEW_ORDER"
            ? "New Order"
            : row.original.status === "COMPLETED"
              ? "Completed"
              : row.original.status === "ACCEPTED"
                ? "Approved"
                : row.original.status === "REJECTED"
                  ? "Rejected"
                  : row.original.status === "CANCELLED"
                    ? "Cancelled"
                    : row.original.status === "ASSIGNED"
                      ? "Assigned"
                      : row.original.status === "COMPLETION_REQUEST"
                        ? "Completed Request"
                        : "In Progress"}
        </Badge>
      ),
    },
  ];

  const renderToolbar = () => (
    <>
      <DatePickerWithRange
        title="Filter by pickup time"
        date={{ from: selectedDate, to: endDate }}
        // presets={presets}
        setDate={(range) => {
          if (range?.from) setSelectedDate(range?.from);
          if (range?.to) setEndDate(range?.to);
          // if (range) {
          //   setDateRange(range);
          // }
        }}
      />

      <Input
        placeholder={`Filter by client`}
        onChange={(e: React.FormEvent<HTMLInputElement>) =>
          setClient(e.currentTarget.value)
        }
        className="w-[200px] lg:w-[250px] h-8"
      />
    </>
  );

  const handleFilterChange = (status: string) => {
    setSelectedTab(status);
  };

  return (
    <ResourceContextProvider value="taxi-orders">
      <CommonList
        filters={
          selectedTab !== "ALL"
            ? {
                status: selectedTab,
                createdDateFrom: selectedDate
                  ? startOfDay(selectedDate).toISOString()
                  : undefined,
                createdDateTo: endDate
                  ? endOfDay(endDate).toISOString()
                  : undefined,
                client,
              }
            : {
                createdDateFrom: selectedDate
                  ? startOfDay(selectedDate).toISOString()
                  : undefined,
                createdDateTo: endDate
                  ? endOfDay(endDate).toISOString()
                  : undefined,
                client,
              }
        }
      >
        <OrdersListHeader
          statusMenu={statusMenu}
          handleFilterChange={handleFilterChange}
          selectedTab={selectedTab}
          totalOrders={totalOrders}
        />
        <CommonTableList
          filterSearch="client"
          columns={columns}
          rowClick={handleRowClick}
          useListContextFilter={true}
          isExportButton={true}
          renderToolbarFilters={renderToolbar}
        />
      </CommonList>
      {selectedOrder && (
        <OrderDetails
          selectedOrder={selectedOrder}
          isSheetOpen={isSheetOpen}
          setIsSheetOpen={setIsSheetOpen}
        />
      )}
    </ResourceContextProvider>
  );
};

export default OrdersList;
